import type { AxiosError } from "axios";

export type Failure = {
  errorCode: string;
  errorMessage: string;
  propertyName: string;
};

type Options = {
  /**
   * If true, will return the raw error message from the error object instead of only the error messages from the `failures` array.
   */
  allowRawErrorMessages?: boolean;
  allowReadableRawMessage?: boolean;
  fallbackErrorMessage?: string;
};

/**
 * Get error message from error object from the backend.
 * If the error object has a `failures` array, returns the `errorMessage` from each failure.
 * If the `failures` array is empty, returns a "Something went wrong" message.
 */
export function getErrorMessage(
  error: unknown,
  options: Options = {
    allowRawErrorMessages: false,
    allowReadableRawMessage: false,
  },
) {
  const failureMessages: string[] = [];

  const axiosError = error as AxiosError;
  if (!axiosError) {
    return failureMessages;
  }

  const errorResponseData = axiosError?.response?.data as
    | { failures: Failure[] }
    | undefined;

  if (errorResponseData?.failures) {
    const failures = errorResponseData.failures;

    failures.forEach((failure) => {
      failureMessages.push(failure.errorMessage);
    });
  }

  if (options.allowRawErrorMessages) {
    failureMessages.push(axiosError.message);
  }

  if (options.allowReadableRawMessage) {
    failureMessages.push(axiosError.message.replace("AxiosError: ", ""));
  }

  if (failureMessages.length === 0) {
    failureMessages.push(
      options.fallbackErrorMessage || "Something went wrong",
    );
  }

  return failureMessages;
}
